import React, { Suspense, useState } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { essentialLocales, setAppLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { ApolloProvider } from '@fs/zion-graphql'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { AppLayout } from '@fs/zion-ui'
import { HelperButton } from '@fs/zion-helper-mode'
import { useEffectiveUser } from '@fs/zion-user'

import { NoticeLoading } from '@fs/zion-icon'
import { GroupsProvider } from '@fs/zion-tree-groups'
import RebuggerClient from '@fs/zion-rebugger/AppClient'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import { beforeSend } from './sentryConfig'
import FeedbackButton from './components/FeedbackButton'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: 1,
      replaysSessionSampleRate: 0.001,
      replaysOnErrorSampleRate: 1,
      enableLogs: true,
      ignoreErrors: [],
      allowUrls: [],
      denyUrls: [],
      beforeSend,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const EndSlot = () => {
  const { signedIn } = useEffectiveUser()
  return (
    <>
      <FeedbackButton />
      {signedIn && <HelperButton emphasis="low" asChip showIcon />}
    </>
  )
}

// Add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  // Enable 30 essential locales
  setAppLocales(essentialLocales)
  const [hideHeader, setHideHeader] = useState(false)
  const [hideFooter, setHideFooter] = useState(false)
  const [hideChatbot, setHideChatbot] = useState(false)
  const [hideSubnav, setHideSubnav] = useState(false)
  const [rootFullScreen, setRootFullScreen] = useState(false)
  const [subHeaderItems, setSubHeaderItems] = useState([])

  return (
    <ApolloProvider>
      <Router basename={base}>
        <Root
          header={{
            currentApp: 'memories',
            hidden: hideHeader,
            showSubHeader: !hideSubnav,
            items: subHeaderItems,
            endSlot: <EndSlot />,
          }}
          footer={{ hidden: hideFooter }}
          fullScreen={rootFullScreen}
          helpChatbot={!hideChatbot}
          analytics
        >
          <AppLayout fullWidth>
            <GroupsProvider>
              <App
                setHideHeader={setHideHeader}
                setHideFooter={setHideFooter}
                setHideSubnav={setHideSubnav}
                setRootFullScreen={setRootFullScreen}
                setHideChatbot={setHideChatbot}
                setSubHeaderItems={setSubHeaderItems}
              />
            </GroupsProvider>
          </AppLayout>
        </Root>
      </Router>
    </ApolloProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
        {process.env.NODE_ENV === 'development' && <RebuggerClient />}
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
